import { useState } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, DollarSign, Banknote } from 'lucide-react';
import { Appointment } from '@/lib/appointments-service';

interface ManualPaymentDialogProps {
  appointment: Appointment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPaymentCreated: () => void;
}

const OFFLINE_PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash / Efectivo' },
  { value: 'card_terminal', label: 'Card Terminal / Terminal de tarjeta' },
  { value: 'bank_transfer', label: 'Bank Transfer / Transferencia bancaria' },
  { value: 'check', label: 'Check / Cheque' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'other', label: 'Other / Otro' },
];

const ManualPaymentDialog = ({
  appointment,
  open,
  onOpenChange,
  onPaymentCreated,
}: ManualPaymentDialogProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    currency: 'USD',
    paymentMethod: '',
    paymentDate: format(new Date(), 'yyyy-MM-dd'),
    notes: '',
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      amount: '',
      currency: 'USD',
      paymentMethod: '',
      paymentDate: format(new Date(), 'yyyy-MM-dd'),
      notes: '',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!appointment) return;

    // Validation
    if (!formData.amount || !formData.paymentMethod) {
      toast({
        title: 'Validation Error',
        description: 'Amount and payment method are required',
        variant: 'destructive',
      });
      return;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a valid amount',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      // Convert amount to cents for storage
      const amountInCents = Math.round(amount * 100);

      // Update the booking with payment information
      const { error } = await supabase
        .from('bookings')
        .update({
          payment_amount: amountInCents,
          payment_currency: formData.currency,
          payment_method: formData.paymentMethod,
          payment_source: 'offline',
          payment_status: 'paid',
          payment_date: new Date(formData.paymentDate).toISOString(),
          payment_notes: formData.notes || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', appointment.id);

      if (error) throw error;

      toast({
        title: 'Payment Registered',
        description: `Manual payment of $${amount.toFixed(2)} has been recorded successfully`,
      });

      resetForm();
      onOpenChange(false);
      onPaymentCreated();
    } catch (error) {
      console.error('Error registering manual payment:', error);
      toast({
        title: 'Error',
        description: 'Failed to register payment. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!appointment) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) resetForm();
      onOpenChange(isOpen);
    }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="h-5 w-5" />
            Register Manual Payment
          </DialogTitle>
          <DialogDescription>
            Record an offline payment for this appointment (cash, card terminal, etc.)
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Client Info Summary */}
          <div className="p-3 bg-muted rounded-lg">
            <p className="font-medium">{appointment.full_name}</p>
            <p className="text-sm text-muted-foreground">{appointment.service}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Booking ID: {appointment.booking_id}
            </p>
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="amount">Amount *</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => handleInputChange('amount', e.target.value)}
                placeholder="150.00"
                className="pl-9"
                required
              />
            </div>
          </div>

          {/* Currency */}
          <div className="space-y-2">
            <Label htmlFor="currency">Currency</Label>
            <Select 
              value={formData.currency} 
              onValueChange={(value) => handleInputChange('currency', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD - US Dollar</SelectItem>
                <SelectItem value="MXN">MXN - Mexican Peso</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Payment Method */}
          <div className="space-y-2">
            <Label htmlFor="paymentMethod">Payment Method *</Label>
            <Select 
              value={formData.paymentMethod} 
              onValueChange={(value) => handleInputChange('paymentMethod', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select method..." />
              </SelectTrigger>
              <SelectContent>
                {OFFLINE_PAYMENT_METHODS.map((method) => (
                  <SelectItem key={method.value} value={method.value}>
                    {method.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Payment Date */}
          <div className="space-y-2">
            <Label htmlFor="paymentDate">Payment Date</Label>
            <Input
              id="paymentDate"
              type="date"
              value={formData.paymentDate}
              onChange={(e) => handleInputChange('paymentDate', e.target.value)}
              max={format(new Date(), 'yyyy-MM-dd')}
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Receipt number, reference, or other details..."
              rows={2}
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Registering...
                </>
              ) : (
                <>
                  <DollarSign className="h-4 w-4 mr-2" />
                  Register Payment
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ManualPaymentDialog;
